<template>
	<div class="mel">
		<!-- /////////////////////// -->
		<!-- OVERLAY -->
		<div @click="onClose" class="mel__overlay"></div>

		<!-- /////////////////////// -->
		<!-- MODAL -->
		<div class="mel__modal">
			<!-- ~~~~~~~~~~~~~~~~~~ -->
			<!-- Close icon -->
			<div class="mel__modal-close">
				<IconClose @click="onClose" class="mel__modal-close-icon" />
			</div>

			<!-- ~~~~~~~~~~~~~~~~~~ -->
			<!-- Title -->
			<div class="mel__modal-title">Последние действия</div>

			<div class="mel__modal-content">
				<div class="mel__modal-content-wr">
					<!-- /////////////// -->
					<!-- Table head -->
					<div class="mel__modal-content-wr-th">
						<div class="mel__modal-content-wr-th-head">
							Пользователь
						</div>
						<div class="mel__modal-content-wr-th-head">Поле</div>
						<div class="mel__modal-content-wr-th-head">
							Старое значение
						</div>
						<div class="mel__modal-content-wr-th-head">
							Новое значение
						</div>
						<div class="mel__modal-content-wr-th-head">Время</div>
					</div>

					<!-- /////////////// -->
					<!-- Table body -->
					<div
						class="mel__modal-content-wr-tb"
						v-for="(item, index) in eventLog"
						:key="index"
					>
						<div class="mel__modal-content-wr-tb-body">
							{{ item.user_name }}
						</div>
						<div class="mel__modal-content-wr-tb-body">
							{{ item.field }}
						</div>
						<div class="mel__modal-content-wr-tb-body">
							{{ item.old_value }}
						</div>
						<div class="mel__modal-content-wr-tb-body">
							{{ item.new_value }}
						</div>
						<div class="mel__modal-content-wr-tb-body">
							{{ dataTimeFormat(item.update_stamp) }}
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { Options, Vue } from "vue-class-component";
import IconClose from "@/components/icons/IconClose.vue";
import { mapActions, mapGetters } from "vuex";
import { IModal } from "@/interfaces/IModal";
import { IEventLog } from "@/interfaces/IEventLog";
import dayjs from "dayjs";
import "dayjs/locale/ru";
dayjs.locale("ru");

@Options({
	components: { IconClose },

	computed: {
		...mapGetters("instanceModule", {
			eventLog: "eventLogGetters",
		}),
	},

	methods: {
		...mapActions("instanceModule", [
			"modalEventLogAction",
			"eventLogAction",
		]),
	},
})
export default class ModalEventLog extends Vue {
	// ************************************************************
	// Define mapGetters
	public eventLog: Array<IEventLog>;

	// ************************************************************
	// Define mapActions
	public modalEventLogAction!: (payload: IModal) => void;
	public eventLogAction!: (payload: { instanceId: number }) => Promise<void>;

	/**
	 * Hook mounted
	 */
	public async mounted() {
		document.body.style.overflowY = "hidden";
		const instanceId = Number(this.$route.params.id);
		await this.eventLogAction({ instanceId: instanceId });
	}

	/**
	 * Закрываем модалку
	 */
	public onClose() {
		document.body.style.overflowY = "auto";
		this.modalEventLogAction({ isShow: false });
	}

	/**
	 * Форматируем timestamp к виду - 15 августа 10:00
	 * @param timestamp
	 */
	public dataTimeFormat(timestamp: string) {
		return dayjs(timestamp).format("DD MMMM HH:mm");
	}
}
</script>

<style scoped lang="scss">
.mel {
	&__overlay {
		display: block;
		position: fixed;
		z-index: 1;
		left: 0;
		top: 0;
		width: 100%;
		height: 100%;
		overflow: auto;
		background-color: rgba(0, 0, 0, 0.4);
	}

	&__modal {
		padding: 8px 24px;
		display: block;
		position: fixed;
		z-index: 2;
		left: 20px;
		right: 20px;
		top: 70px;
		bottom: 50px;

		background-color: rgb(255, 255, 255);
		border: 1px solid rgb(255, 255, 255);
		border-radius: 12px;
	}

	&__modal-close {
		display: flex;
		justify-content: flex-end;
		padding: 16px 16px 0;
	}

	&__modal-close-icon {
		cursor: pointer;
	}

	&__modal-title {
		display: flex;
		justify-content: center;
		padding: 0 0 16px;
		color: rgb(49, 49, 50);
		font-size: 18px;
		font-weight: 600;
	}

	&__modal-content {
		display: block;
		position: relative;
	}

	&__modal-content-wr {
		position: fixed;
		overflow-y: scroll;
		scrollbar-width: thin;
		left: 30px;
		right: 30px;
		top: 150px;
		bottom: 60px;
	}

	&__modal-content-wr-th {
		display: grid;
		grid-template-columns:
			minmax(90px, 1fr)
			minmax(150px, 1fr)
			minmax(70px, 1fr)
			minmax(70px, 1fr)
			minmax(80px, 1fr);

		background: rgb(252, 252, 252);
	}

	&__modal-content-wr-tb {
		display: grid;
		grid-template-columns:
			minmax(90px, 1fr)
			minmax(150px, 1fr)
			minmax(70px, 1fr)
			minmax(70px, 1fr)
			minmax(80px, 1fr);

		background: rgb(252, 252, 252);
	}

	&__modal-content-wr-th-head {
		color: rgb(119, 121, 124);
		font-size: 16px;
		border-right: 1px solid rgb(219, 219, 219);
		padding: 22px;
	}

	&__modal-content-wr-th-head:last-child {
		border-right: 0;
	}

	&__modal-content-wr-tb-body {
		display: flex;
		color: rgb(34, 34, 34);
		font-size: 16px;
		border-right: 1px solid rgb(219, 219, 219);
		padding: 14px;
		align-items: center;
	}

	&__modal-content-wr-tb-body:last-child {
		border-right: 0;
	}

	// Desktop
	@media screen and (min-width: 768px) {
		&__modal-content-wr-th {
			border: 1px solid rgb(219, 219, 219);
			border-top-left-radius: 12px 12px;
			border-top-right-radius: 12px 12px;
			border-bottom: 0;
		}

		&__modal-content-wr-tb {
			border: 1px solid rgb(219, 219, 219);
			border-bottom: 0;
		}

		&__modal-content-wr-tb:last-child {
			border: 1px solid rgb(219, 219, 219);
			border-bottom-left-radius: 12px 12px;
			border-bottom-right-radius: 12px 12px;
		}
	}

	// Mob
	@media screen and (max-width: 768px) {
		&__modal-content-wr-th-head {
			font-size: 12px;
			padding: 6px;
		}

		&__modal-content-wr-tb-body {
			font-size: 12px;
			padding: 6px;
		}
	}
}
</style>
